import { motion } from "framer-motion";
import { TreePine, Home, Droplets, Megaphone, Lock, Coins, Copy, ExternalLink, HeartHandshake, Cog } from "lucide-react";
import { toast } from "sonner";

const CONTRACT_ADDRESS = "0x246502B687a23fD4b37eD362263f6a52aF5C01C5";
const scanUrl = (addr) => `https://basescan.org/address/${addr}`;
const short = (addr) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const CHARITY_WALLET = "0xA7daEfe1522b3868874F79db32290b5bdd2EC2a2";
const OPERATIONS_WALLET = "0x43D1Fe8BBC3113ccD43094bdF5a044C8BcFA5bBa";
const LIQUIDITY_WALLET = "0x8bbfFCE68baBF5881C7B9BA17B68FD3c40a7a89B";

const allocations = [
  { label: "Reforestation Fund", pct: 40, icon: TreePine, color: "#34D399", span: "md:col-span-7", tall: true },
  { label: "Community Rebuild", pct: 25, icon: Home, color: "#FF5A1F", span: "md:col-span-5" },
  { label: "Liquidity Pool", pct: 15, icon: Droplets, color: "#60A5FA", span: "md:col-span-5" },
  { label: "Marketing & Growth", pct: 10, icon: Megaphone, color: "#FBBF24", span: "md:col-span-4" },
  { label: "Team (locked 24mo)", pct: 6, icon: Lock, color: "#A78BFA", span: "md:col-span-4" },
  { label: "Treasury Reserve", pct: 4, icon: Coins, color: "#8C8A88", span: "md:col-span-4" },
];

const taxSplit = [
  { pct: 3, label: "Automated Wildfire Relief Fund", icon: HeartHandshake, color: "#34D399", wallet: CHARITY_WALLET, testid: "tax-split-charity" },
  { pct: 3, label: "Core Project Operations & Maintenance", icon: Cog, color: "#FF5A1F", wallet: OPERATIONS_WALLET, testid: "tax-split-operations" },
  { pct: 1, label: "Automated Chart Liquidity Injection", icon: Droplets, color: "#60A5FA", wallet: LIQUIDITY_WALLET, testid: "tax-split-liquidity" },
];

export default function Tokenomics() {
  const copy = async (text, label = "Address") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };

  return (
    <section id="tokenomics" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
      <div className="max-w-3xl mb-14">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Tokenomics</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
          10,000,000,000 $AFTBRN — built for impact.
        </h2>

        {/* Live network parameters */}
        <div
          data-testid="token-network-row"
          className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-white/10 bg-card p-4"
        >
          <span className="font-mono text-xs sm:text-sm text-muted-foreground shrink-0">
            <span className="text-ember">Network:</span> Base Layer 2
          </span>
          <span className="hidden sm:block h-4 w-px bg-white/15" />
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-xs sm:text-sm text-muted-foreground shrink-0">
              <span className="text-ember">Contract:</span>
            </span>
            <span className="font-mono text-xs sm:text-sm text-foreground truncate" title={CONTRACT_ADDRESS}>
              {CONTRACT_ADDRESS}
            </span>
            <button
              onClick={() => copy(CONTRACT_ADDRESS, "Contract address")}
              data-testid="copy-contract-btn"
              aria-label="Copy contract address"
              className="shrink-0 inline-flex items-center gap-1 border border-white/15 px-2 py-1 text-muted-foreground hover:border-ember hover:text-ember transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-wider">Copy</span>
            </button>
          </div>
        </div>

        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          Minted on Base with a fixed supply of 10,000,000,000 $AFTBRN. The supply is distributed
          transparently across the mission, liquidity, and a time-locked team — while a{" "}
          <span className="text-foreground font-semibold">7% tax on every trade</span> continuously
          funds charity, operations, and liquidity. Two engines, one mission.
        </p>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-6">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">01 · Supply Allocation</span>
        <span className="font-mono text-xs text-muted-foreground">One-time distribution of the 10B fixed supply</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {allocations.map((a, i) => (
          <motion.div
            key={a.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            className={`${a.span} ${a.tall ? "md:row-span-2" : ""} bg-card border border-white/10 p-8 hover:-translate-y-1 transition-transform duration-300 flex flex-col justify-between min-h-[180px]`}
            data-testid={`token-alloc-${i}`}
          >
            <div className="flex items-start justify-between">
              <div
                className="w-12 h-12 flex items-center justify-center border"
                style={{ borderColor: `${a.color}55` }}
              >
                <a.icon className="w-5 h-5" style={{ color: a.color }} />
              </div>
              <span className="font-display font-bold text-4xl" style={{ color: a.color }}>
                {a.pct}%
              </span>
            </div>
            <div className="mt-6">
              <h3 className="font-display font-bold text-xl">{a.label}</h3>
              <div className="mt-3 h-1.5 bg-white/10">
                <div className="h-full" style={{ width: `${a.pct}%`, background: a.color }} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* 7% transaction tax breakdown + audit ledger */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mt-8 border border-white/10 bg-card p-8 sm:p-10"
        data-testid="tax-breakdown"
      >
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">02 · Transaction Tax</span>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="font-display font-bold text-2xl sm:text-3xl">
            7% <span className="text-ember">on every trade</span>
          </h3>
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Auto-split, forever
          </span>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-6">
          {taxSplit.map((t) => (
            <div key={t.label} className="border border-white/10 p-6 flex flex-col" data-testid={t.testid}>
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 flex items-center justify-center border" style={{ borderColor: `${t.color}55` }}>
                  <t.icon className="w-5 h-5" style={{ color: t.color }} />
                </div>
                <span className="font-display font-bold text-3xl" style={{ color: t.color }}>{t.pct}%</span>
              </div>
              <p className="mt-4 text-muted-foreground leading-snug flex-1">{t.label}</p>
              <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-4">
                <a
                  href={scanUrl(t.wallet)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${t.testid}-scan`}
                  title={t.wallet}
                  className="font-mono text-xs text-muted-foreground hover:text-ember transition-colors inline-flex items-center gap-1 min-w-0"
                >
                  <span className="truncate">{short(t.wallet)}</span>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
                <button
                  onClick={() => copy(t.wallet, "Wallet address")}
                  data-testid={`${t.testid}-copy`}
                  aria-label="Copy wallet address"
                  className="ml-auto shrink-0 border border-white/15 p-1.5 text-muted-foreground hover:border-ember hover:text-ember transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <a
          href={scanUrl(CHARITY_WALLET)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="track-charity-wallet-btn"
          className="mt-8 inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3.5 hover:bg-ember-light transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Track Public Charity Wallet On-Chain
        </a>
      </motion.div>
    </section>
  );
}
